import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Typography,
} from "@mui/material";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import AgentAvatar from "../AgentAvatar";
import { useStatusPalette } from "../../../utils/statusColors";
import useSchedulingStore from "../useSchedulingStore";
import ScheduledMessageDialog, {
  ScheduleStatusBadge,
} from "../ScheduledMessageDialog";
import RecurringScheduleDialog, {
  RecurringScheduleStatusBadge,
  formatRecurrenceDescription,
} from "../RecurringScheduleDialog";
import {
  AVATAR_SIZE,
  AVATAR_RADIUS,
  TEXT_INDENT,
} from "./conversationConstants";
import {
  messageRowSx,
  formatCountdown,
  parseDurationToMs,
  getAgentAvatarClickHandler,
  getClickableAgentAvatarSx,
} from "./conversationUtils";

export const ScheduleMessageCard = function ScheduleMessageCard({
  args,
  result,
  agentName,
  agentId,
  grouped,
  timestamp,
  scheduledMessages,
  onEditScheduledMessage,
  onCancelScheduledMessage,
  onRefreshScheduledMessages,
  onOpenAgentSettings,
}) {
  const _sp = useStatusPalette();
  let mode = null;
  let duration = null;
  let resumeAt = null;
  let message = null;
  let reason = null;
  let scheduleId = null;

  try {
    const parsed = typeof args === "string" ? JSON.parse(args) : args;
    if (parsed) {
      mode = parsed.mode;
      duration = parsed.duration;
      resumeAt = parsed.resumeAt;
      message = parsed.message;
      reason = parsed.reason;
    }
  } catch {}

  try {
    const parsedResult =
      typeof result === "string" ? JSON.parse(result) : result;
    if (parsedResult) {
      scheduleId = parsedResult.scheduleId;
      if (!resumeAt) resumeAt = parsedResult.resumeAt;
    }
  } catch {}

  const scheduleItems = Array.isArray(scheduledMessages)
    ? scheduledMessages
    : useSchedulingStore.getState().scheduledMessages;

  const liveSchedule = scheduleId
    ? scheduleItems.find((s) => s.scheduleId === scheduleId)
    : null;

  if (liveSchedule) {
    message = liveSchedule.message || message;
    reason = liveSchedule.reason || reason;
    resumeAt = liveSchedule.resumeAt || resumeAt;
  }

  const scheduleStatus = liveSchedule?.status;
  const isCancelled = scheduleStatus === "CANCELLED";

  if (!resumeAt && mode === "after_duration" && duration && timestamp) {
    const durationMs = parseDurationToMs(duration);
    if (durationMs > 0) {
      resumeAt = new Date(
        new Date(timestamp).getTime() + durationMs
      ).toISOString();
    }
  }

  const [countdown, setCountdown] = useState(() => formatCountdown(resumeAt));
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const isSent = resumeAt && !formatCountdown(resumeAt);

  useEffect(() => {
    if (!resumeAt) return;
    const tick = () => setCountdown(formatCountdown(resumeAt));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [resumeAt]);

  const timeLabel =
    mode === "after_duration" && duration
      ? `After ${duration}`
      : resumeAt
      ? new Date(resumeAt).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
      : "Scheduled";

  const sent = !isCancelled && isSent && !countdown;
  const isEditable = scheduleStatus === "SCHEDULED";
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);

  const handleEdit = async (sid, payload) => {
    if (onEditScheduledMessage) {
      await onEditScheduledMessage(sid, payload);
    } else {
      await useSchedulingStore.getState().editScheduledMessage(sid, payload);
    }
    if (onRefreshScheduledMessages) {
      onRefreshScheduledMessages();
    } else if (agentId) {
      useSchedulingStore.getState().fetchScheduledMessagesByAgent(agentId);
    }
  };

  const handleCancel = async (sid) => {
    if (onCancelScheduledMessage) {
      await onCancelScheduledMessage(sid);
    } else {
      await useSchedulingStore.getState().cancelScheduledMessage(sid);
    }
    if (onRefreshScheduledMessages) {
      onRefreshScheduledMessages();
    } else if (agentId) {
      useSchedulingStore.getState().fetchScheduledMessagesByAgent(agentId);
    }
  };

  const borderColor = isCancelled ? "divider" : sent ? _sp.green.border : _sp.purple.border;
  const headerBg = isCancelled ? "action.hover" : sent ? _sp.green.bg : _sp.purple.bg;
  const headerColor = isCancelled ? "text.disabled" : sent ? _sp.green.color : _sp.purple.color;
  const iconColor = isCancelled ? "text.disabled" : sent ? _sp.green.color : _sp.purple.color;
  const headerText = isCancelled
    ? "Scheduled message cancelled"
    : sent
    ? "Scheduled message sent"
    : "Scheduled message";

  return (
    <Box sx={{ ...messageRowSx, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!grouped && (
          <AgentAvatar agentId={agentId} name={agentName} size={AVATAR_SIZE} onClick={avatarClick} />
        )}
        <Box
          sx={{
            border: 1,
            borderColor: borderColor,
            borderRadius: "8px",
            overflow: "hidden",
            maxWidth: "75%",
            minWidth: "240px",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              backgroundColor: headerBg,
            }}
          >
            <ScheduleIcon sx={{ fontSize: 16, color: iconColor }} />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: headerColor,
                flex: 1,
              }}
            >
              {headerText}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {isEditable && (
                <>
                  <IconButton
                    size="small"
                    onClick={() => setEditDialogOpen(true)}
                    sx={{
                      p: "3px",
                      color: _sp.purple.color,
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleCancel(scheduleId)}
                    sx={{
                      p: "3px",
                      color: _sp.red.color,
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <CancelOutlinedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </>
              )}
              {isCancelled ? (
                <ScheduleStatusBadge status="CANCELLED" />
              ) : sent ? (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    height: "20px",
                    px: "8px",
                    borderRadius: "5px",
                    backgroundColor: _sp.green.border,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: _sp.green.color,
                      lineHeight: 1,
                    }}
                  >
                    Sent
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      height: "20px",
                      px: "8px",
                      borderRadius: "5px",
                      backgroundColor: _sp.purple.border,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 500,
                        color: _sp.purple.color,
                        lineHeight: 1,
                      }}
                    >
                      {timeLabel}
                    </Typography>
                  </Box>
                  {countdown && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        height: "20px",
                        px: "8px",
                        borderRadius: "5px",
                        backgroundColor: "action.hover",
                        border: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#7c3aed",
                          lineHeight: 1,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {countdown}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
          {message && (
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: isCancelled ? "text.disabled" : "text.primary",
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  textDecoration: isCancelled ? "line-through" : "none",
                }}
              >
                {message}
              </Typography>
            </Box>
          )}
          {reason && (
            <Box sx={{ px: 1.5, pb: 0.75 }}>
              <Typography
                sx={{ fontSize: "11px", color: "text.disabled", fontStyle: "italic" }}
              >
                {reason}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {scheduleId && (
        <ScheduledMessageDialog
          item={
            liveSchedule || {
              scheduleId,
              message,
              resumeAt,
              reason,
              status: sent ? "COMPLETED" : "SCHEDULED",
            }
          }
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onEdit={handleEdit}
          onCancel={handleCancel}
        />
      )}
    </Box>
  );
};

export const RecurringScheduleCard = function RecurringScheduleCard({
  args,
  result,
  agentName,
  agentId,
  grouped,
  onOpenAgentSettings,
}) {
  const _sp = useStatusPalette();
  let recurrenceType = null;
  let interval = null;
  let daysOfWeek = null;
  let timeOfDay = null;
  let timezone = null;
  let message = null;
  let reason = null;
  let scheduleId = null;

  try {
    const parsed = typeof args === "string" ? JSON.parse(args) : args;
    if (parsed) {
      recurrenceType = parsed.recurrenceType;
      interval = parsed.interval;
      daysOfWeek = parsed.daysOfWeek;
      timeOfDay = parsed.timeOfDay;
      timezone = parsed.timezone;
      message = parsed.message;
      reason = parsed.reason;
    }
  } catch {}

  try {
    const parsedResult =
      typeof result === "string" ? JSON.parse(result) : result;
    if (parsedResult) {
      scheduleId = parsedResult.scheduleId;
    }
  } catch {}

  const recurringItems = Array.isArray(
    useSchedulingStore.getState().recurringSchedules
  )
    ? useSchedulingStore.getState().recurringSchedules
    : [];

  const liveSchedule = scheduleId
    ? recurringItems.find((s) => s.scheduleId === scheduleId)
    : null;

  if (liveSchedule) {
    message = liveSchedule.message || message;
    reason = liveSchedule.reason || reason;
    recurrenceType = liveSchedule.recurrenceType || recurrenceType;
    interval = liveSchedule.intervalDuration || interval;
    daysOfWeek = liveSchedule.daysOfWeek || daysOfWeek;
    timeOfDay = liveSchedule.timeOfDay || timeOfDay;
    timezone = liveSchedule.timezone || timezone;
  }

  const scheduleStatus = liveSchedule?.status;
  const isCancelled = scheduleStatus === "CANCELLED";
  const isCompleted = scheduleStatus === "COMPLETED";
  const isPaused = scheduleStatus === "PAUSED";
  const isActive = scheduleStatus === "ACTIVE" || scheduleStatus === "FIRING";
  const isEditable = isActive || isPaused;
  const maxOccurrences = liveSchedule?.maxOccurrences;
  const endAt = liveSchedule?.endAt;

  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const recurrenceDesc = formatRecurrenceDescription({
    recurrenceType,
    intervalDuration: interval,
    daysOfWeek,
    timeOfDay,
  });

  const nextOccurrence = liveSchedule?.nextResumeAt;
  const occurrenceCount = liveSchedule?.occurrenceCount ?? 0;

  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);

  const handleEdit = async (sid, payload) => {
    await useSchedulingStore.getState().editRecurringSchedule(sid, payload);
    if (agentId) {
      useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
    }
  };

  const handleCancel = async (sid) => {
    await useSchedulingStore.getState().cancelRecurringSchedule(sid);
    if (agentId) {
      useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
    }
  };

  const handlePause = async (sid) => {
    await useSchedulingStore.getState().pauseRecurringSchedule(sid);
    if (agentId) {
      useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
    }
  };

  const handleResume = async (sid) => {
    await useSchedulingStore.getState().resumeRecurringSchedule(sid);
    if (agentId) {
      useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
    }
  };

  const borderColor = isCancelled || isCompleted
    ? "divider"
    : isPaused
    ? _sp.amber.border
    : _sp.teal.border;
  const headerBg = isCancelled || isCompleted
    ? "action.hover"
    : isPaused
    ? _sp.amber.bg
    : _sp.teal.bg;
  const headerColor = isCancelled
    ? "text.disabled"
    : isCompleted
    ? _sp.indigo.color
    : isPaused
    ? _sp.amber.color
    : _sp.teal.color;
  const iconColor = headerColor;
  const headerText = isCancelled
    ? "Recurring schedule cancelled"
    : isCompleted
    ? "Recurring schedule completed"
    : isPaused
    ? "Recurring schedule paused"
    : "Recurring schedule";

  return (
    <Box sx={{ ...messageRowSx, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!grouped && (
          <AgentAvatar agentId={agentId} name={agentName} size={AVATAR_SIZE} onClick={avatarClick} />
        )}
        <Box
          sx={{
            border: 1,
            borderColor: borderColor,
            borderRadius: "8px",
            overflow: "hidden",
            maxWidth: "75%",
            minWidth: "240px",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              backgroundColor: headerBg,
            }}
          >
            <EventRepeatOutlinedIcon sx={{ fontSize: 16, color: iconColor }} />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: headerColor,
                flex: 1,
              }}
            >
              {headerText}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {isEditable && (
                <>
                  <IconButton
                    size="small"
                    onClick={() => setEditDialogOpen(true)}
                    sx={{
                      p: "3px",
                      color: _sp.teal.color,
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                  {isActive && (
                    <IconButton
                      size="small"
                      onClick={() => handlePause(scheduleId)}
                      sx={{
                        p: "3px",
                        color: _sp.amber.color,
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                    >
                      <PauseCircleOutlinedIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleCancel(scheduleId)}
                    sx={{
                      p: "3px",
                      color: _sp.red.color,
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <CancelOutlinedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </>
              )}
              {isCancelled ? (
                <RecurringScheduleStatusBadge status="CANCELLED" />
              ) : isCompleted ? (
                <RecurringScheduleStatusBadge status="COMPLETED" />
              ) : isPaused ? (
                <RecurringScheduleStatusBadge status="PAUSED" />
              ) : (
                <>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      height: "20px",
                      px: "8px",
                      borderRadius: "5px",
                      backgroundColor: _sp.teal.border,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 500,
                        color: _sp.teal.color,
                        lineHeight: 1,
                      }}
                    >
                      {recurrenceDesc}
                    </Typography>
                  </Box>
                  {(occurrenceCount > 0 || maxOccurrences != null) && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        height: "20px",
                        px: "8px",
                        borderRadius: "5px",
                        backgroundColor: "action.hover",
                        border: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: _sp.teal.color,
                          lineHeight: 1,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {occurrenceCount}{maxOccurrences != null ? `/${maxOccurrences}` : 'x'} fired
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
          {message && (
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: isCancelled ? "text.disabled" : "text.primary",
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  textDecoration: isCancelled ? "line-through" : "none",
                }}
              >
                {message}
              </Typography>
            </Box>
          )}
          {reason && (
            <Box sx={{ px: 1.5, pb: 0.75 }}>
              <Typography
                sx={{ fontSize: "11px", color: "text.disabled", fontStyle: "italic" }}
              >
                {reason}
              </Typography>
            </Box>
          )}
          {nextOccurrence && isActive && (
            <Box sx={{ px: 1.5, pb: 0.75 }}>
              <Typography
                sx={{ fontSize: "11px", color: "text.disabled" }}
              >
                Next: {new Date(nextOccurrence).toLocaleString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {scheduleId && (
        <RecurringScheduleDialog
          item={
            liveSchedule || {
              scheduleId,
              message,
              reason,
              recurrenceType,
              intervalDuration: interval,
              daysOfWeek,
              timeOfDay,
              timezone,
              status: "ACTIVE",
              occurrenceCount: 0,
            }
          }
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onEdit={handleEdit}
          onCancel={handleCancel}
          onPause={handlePause}
          onResume={handleResume}
        />
      )}
    </Box>
  );
};

export function ToolCallCard({
  toolName,
  args,
  result,
  agentName,
  agentId,
  grouped,
  onOpenAgentSettings,
  lifecycleState,
  compact = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);
  const isExecuting =
    lifecycleState === "input-streaming" || lifecycleState === "input-available";
  const isError = lifecycleState === "output-error";

  const formatJson = (val) => {
    if (!val) return "";
    if (typeof val === "string") {
      try {
        return JSON.stringify(JSON.parse(val), null, 2);
      } catch {
        return val;
      }
    }
    return JSON.stringify(val, null, 2);
  };

  const isTruncated =
    typeof result === "string" && result.endsWith("(truncated)");

  return (
    <Box sx={{ ...messageRowSx, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!grouped && (
          <AgentAvatar agentId={agentId} name={agentName} size={AVATAR_SIZE} onClick={avatarClick} />
        )}
        <Box
          sx={{
            border: "1px solid",
            borderColor: isError ? "error.main" : "divider",
            borderRadius: "8px",
            overflow: "hidden",
            maxWidth: "75%",
            minWidth: compact ? "auto" : "240px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1.5,
              py: 0.75,
              backgroundColor: "action.hover",
              ...(!compact && { cursor: "pointer" }),
            }}
            onClick={compact ? undefined : () => setExpanded(!expanded)}
          >
            <BuildOutlinedIcon
              sx={{ fontSize: 14, color: "text.disabled", mr: 0.75 }}
            />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                fontFamily: "monospace",
                color: "text.primary",
                flex: 1,
              }}
            >
              {toolName}
            </Typography>
            {isExecuting && !result && (
              <CircularProgress size={12} thickness={5} sx={{ ml: 0.5 }} />
            )}
            {!compact && isTruncated && !expanded && (
              <Typography
                sx={{
                  fontSize: "10px",
                  color: "text.disabled",
                  fontStyle: "italic",
                  mr: 0.5,
                }}
              >
                truncated
              </Typography>
            )}
            {!compact && (
              <ExpandMoreIcon
                sx={{
                  fontSize: 16,
                  color: "text.disabled",
                  transition: "transform 0.2s",
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            )}
          </Box>

          {!compact && (
            <Collapse in={expanded}>
              {args != null && (
                <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 500,
                      color: "text.disabled",
                      px: 1.5,
                      pt: 0.75,
                      pb: 0.25,
                    }}
                  >
                    Arguments
                  </Typography>
                  <Box
                    sx={{
                      mx: 1.5,
                      mb: 1,
                      p: 1,
                      backgroundColor: "action.hover",
                      borderRadius: "6px",
                      maxHeight: "160px",
                      overflowY: "auto",
                      scrollbarWidth: "thin",
                      scrollbarColor: (theme) => `${theme.palette.divider} transparent`,
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        fontSize: "11px",
                        fontFamily: "monospace",
                        color: "text.primary",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        m: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      {formatJson(args)}
                    </Typography>
                  </Box>
                </Box>
              )}
              {result != null && (
                <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 500,
                      color: "text.disabled",
                      px: 1.5,
                      pt: 0.75,
                      pb: 0.25,
                    }}
                  >
                    Result
                  </Typography>
                  <Box
                    sx={{
                      mx: 1.5,
                      mb: 1,
                      p: 1,
                      backgroundColor: "action.hover",
                      borderRadius: "6px",
                      maxHeight: "160px",
                      overflowY: "auto",
                      scrollbarWidth: "thin",
                      scrollbarColor: (theme) => `${theme.palette.divider} transparent`,
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        fontSize: "11px",
                        fontFamily: "monospace",
                        color: "text.primary",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        m: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      {formatJson(result)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Collapse>
          )}
        </Box>
      </Box>
    </Box>
  );
}
